<script setup>
import {reactive, computed} from 'vue'
import Button from "@/common/Button.vue"
import api from '@/services/api'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useTemplatesStore} from '@/stores/templates'
import {useModalsStore} from '@/stores/modals'

const props = defineProps({
    options: {
        type: Object,
        required: true
    },
})

const emit = defineEmits(['hide-modal'])

// Pinia Stores
const pageContentDataStore = usePageContentDataStore()
const templatesStore = useTemplatesStore()
const modalsStore = useModalsStore()

// --- State ---

const formData = reactive({
    title: '',
    contentType: '',
})

// --- Computed ---

const isDisabled = computed(() => !formData.title || !formData.contentType)

const selectOptions = computed(() => {
    // Map directly from Pinia store getters
    const types = (templatesStore.contentTypes || []).map(o => ({
        value: o.id,
        text: o.title
    }))

    // Sort alphabetically
    types.sort((a, b) => a.text.localeCompare(b.text))

    // Prepend the default "Select..." option
    return [{value: '', text: 'Select…'}, ...types]
})

// --- Methods ---

const setContentId = (contentId) => {
    if (props.options.onSelect) {
        props.options.onSelect({contentId})
    }

    emit('hide-modal')
}

const saveNewContent = async () => {
    if (isDisabled.value) return

    try {
        const response = await api.post('api/component/content', {
            title: formData.title,
            contentType: formData.contentType,
        })

        if (response.data?.id) {
            setContentId(response.data.id)
        }
    } catch (error) {
        console.error("Failed to save content", error)
    }
}

const showSiteTreeBrowser = () => {
    modalsStore.show({
        componentName: 'SiteTreeModal',
        obj: {
            selectContentMode: true,
            onContentSelect: (pageContentId) => {
                const item = pageContentDataStore.items.find(o => o.id === pageContentId)
                if (item) setContentId(item.contentId)
            }
        }
    })
}

const showSelectList = () => {
    const modal = modalsStore.show({
        componentName: 'SelectListModal',
        obj: {
            listingName: 'content.foo', // namespace prevents listing from auto-loading
            selectedIds: [],
            onSelect: (newIds) => {
                const selectedId = newIds.pop()
                if (selectedId) {
                    setContentId(selectedId)
                    if (modal?.id !== undefined) {
                        modalsStore.remove(modal.id)
                    }
                }
            }
        }
    })
}
</script>

<template>
    <div class="modal-dialog modal-lg add-content-modal">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Add content</h4>
                <button
                    aria-label="Close"
                    class="btn-close"
                    type="button"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <form class="form form-horizontal" @submit.prevent>
                    <fieldset>
                        <legend>New content</legend>

                        <div class="mb-3 mt-3 row">
                            <label class="col-sm-3 form-label">Title</label>
                            <div class="col-sm-9">
                                <input v-model="formData.title" v-focus class="form-control">
                            </div>
                        </div>

                        <div class="mb-3 row">
                            <label class="col-sm-3 form-label">Content type</label>
                            <div class="col-sm-9">
                                <select v-model="formData.contentType" class="form-select">
                                    <option v-for="option in selectOptions" :key="option.value" :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="mb-3 row">
                            <div class="offset-sm-3 col-sm-9">
                                <Button
                                    class="border"
                                    :disabled="isDisabled"
                                    :active-value="!isDisabled"
                                    @click="saveNewContent"
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="mt-4">
                        <legend>Existing content</legend>

                        <div class="mb-3 row">
                            <div class="col-sm-9 offset-sm-3">
                                <div class="btn-group">
                                    <Button class="border" @click="showSiteTreeBrowser">
                                        <i class="bi-diagram-3-fill"/>
                                    </Button>
                                    <Button class="border" @click="showSelectList">
                                        Browse&hellip;
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </fieldset>
                </form>
            </div>
        </div>
    </div>
</template>

<style scoped>
.add-content-modal legend {
    font-size: 16px;
    font-weight: 400;
}
</style>